import { LinearProgress } from '@mui/material';
import { flexRender } from '@tanstack/react-table';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { Table } from 'react-bootstrap';

const MIN_PROGRESS_VISIBLE_MS = 420;

const kickListeners = new Set();
let lastSearchKickAt = 0;

/** 用户按下搜索后立即显示进度（早于 RTK isFetching） */
export const kickWorkbenchMobileListSearchProgress = () => {
	lastSearchKickAt = Date.now();
	kickListeners.forEach((listener) => listener());
};

const useSearchKickSignal = () => {
	const [kickAt, setKickAt] = useState(lastSearchKickAt);

	useEffect(() => {
		const onKick = () => setKickAt(lastSearchKickAt);
		kickListeners.add(onKick);
		return () => kickListeners.delete(onKick);
	}, []);

	return kickAt;
};

/** 列表是否在请求中（meta / MRT showProgressBars / TanStack meta.isLoading） */
export const getTableListFetchingState = (table) => {
	if (!table) {
		return { isLoading: false, isFetching: false };
	}

	const { isLoading: stateLoading, showProgressBars } = table.getState();
	const isLoading = Boolean(stateLoading ?? table.options.meta?.isLoading);
	const isFetching = Boolean(table.options.meta?.isFetching ?? showProgressBars);

	return { isLoading, isFetching };
};

export const getTablePageTurnKey = (table) => {
	const pagination = table?.getState?.()?.pagination;
	return `${pagination?.pageIndex ?? 0}:${pagination?.pageSize ?? 0}`;
};

/** 请求中显示进度条；结束后至少保留 MIN_PROGRESS_VISIBLE_MS，避免一闪而过 */
export const useWorkbenchMobileListProgressVisible = (isFetching, isLoading, pageTurnKey = '') => {
	const searchKickAt = useSearchKickSignal();
	const [kickPulse, setKickPulse] = useState(0);
	const [pagePulse, setPagePulse] = useState(0);
	const [visible, setVisible] = useState(false);
	const hideTimerRef = useRef(null);
	const startedRef = useRef(0);
	const wasBusyRef = useRef(false);
	const pageTurnKeyRef = useRef(pageTurnKey);

	useEffect(() => {
		if (!searchKickAt) {
			return undefined;
		}
		setKickPulse(searchKickAt);
		const timer = window.setTimeout(() => setKickPulse(0), MIN_PROGRESS_VISIBLE_MS);
		return () => window.clearTimeout(timer);
	}, [searchKickAt]);

	useEffect(() => {
		if (pageTurnKeyRef.current === pageTurnKey) {
			return undefined;
		}
		pageTurnKeyRef.current = pageTurnKey;
		if (isLoading) {
			return undefined;
		}
		setPagePulse(Date.now());
		const timer = window.setTimeout(() => setPagePulse(0), MIN_PROGRESS_VISIBLE_MS);
		return () => window.clearTimeout(timer);
	}, [pageTurnKey, isLoading]);

	const busy = isLoading || isFetching || kickPulse > 0 || pagePulse > 0;

	useEffect(() => {
		if (hideTimerRef.current) {
			window.clearTimeout(hideTimerRef.current);
			hideTimerRef.current = null;
		}

		if (busy) {
			if (!wasBusyRef.current) {
				startedRef.current = Date.now();
			}
			wasBusyRef.current = true;
			setVisible(true);
			return undefined;
		}

		if (!wasBusyRef.current) {
			return undefined;
		}

		wasBusyRef.current = false;
		const remain = Math.max(0, MIN_PROGRESS_VISIBLE_MS - (Date.now() - startedRef.current));
		hideTimerRef.current = window.setTimeout(() => {
			setVisible(false);
			hideTimerRef.current = null;
		}, remain);

		return undefined;
	}, [busy]);

	useEffect(
		() => () => {
			if (hideTimerRef.current) {
				window.clearTimeout(hideTimerRef.current);
			}
		},
		[],
	);

	return visible;
};

export const useWorkbenchListFetchingVisible = (table) => {
	const { isLoading, isFetching } = getTableListFetchingState(table);
	return useWorkbenchMobileListProgressVisible(isFetching, isLoading, getTablePageTurnKey(table));
};

const progressSx = {
	width: '100%',
	height: 4,
	flexShrink: 0,
	borderRadius: 999,
	backgroundColor: 'rgba(var(--app-theme-primary-rgb, 226, 155, 56), 0.2)',
	'& .MuiLinearProgress-bar': {
		borderRadius: 999,
		backgroundColor: 'var(--app-home-topbar-accent, var(--app-theme-primary, var(--bs-primary)))',
	},
};

export const WorkbenchListProgressBar = ({ visible, className }) => {
	if (!visible) {
		return null;
	}

	return <LinearProgress className={className} color='primary' aria-hidden sx={progressSx} />;
};

/** 手机列表区进度条（条数栏下方） */
export const WorkbenchMobileListProgress = ({ table, className }) => {
	const visible = useWorkbenchListFetchingVisible(table);
	return <WorkbenchListProgressBar visible={visible} className={className} />;
};

const resolveSkeletonBarWidth = (column) => {
	const size = Number(column.getSize?.() || column.columnDef.size || 0);
	const minWidth = Number(column.columnDef.meta?.headerProps?.style?.minWidth) || 0;
	const basis = size > 0 ? size : minWidth > 0 ? minWidth : 120;
	// 骨架略短于列宽，窄列更短、宽列按比例拉长
	return Math.round(Math.min(Math.max(basis * 0.52, 36), Math.min(basis * 0.78, 220)));
};

const WorkbenchDesktopRowSkeleton = ({ table }) => {
	const headerGroups = table.getHeaderGroups();
	const leafColumns = table.getVisibleLeafColumns();
	const pageSize = Number(table.getState()?.pagination?.pageSize) || 10;
	const rowCount = Math.min(Math.max(pageSize, 1), 30);

	return (
		<div className='table-responsive scrollbar' role='status' aria-label='加载中'>
			<Table size='sm' striped className='fs-10 mb-0 overflow-hidden w-100'>
				<thead className='bg-200 text-900 text-nowrap align-middle'>
					{headerGroups.map((headerGroup) => (
						<tr key={headerGroup.id}>
							{headerGroup.headers.map((header) => {
								const isGroupHeader = (header.column.columns?.length ?? 0) > 0;
								return (
								<th
									key={header.id}
									colSpan={header.colSpan}
									{...header.column.columnDef.meta?.headerProps}
									className={classNames(header.column.columnDef.meta?.headerProps?.className, 'text-900', {
										'text-center': isGroupHeader,
										'text-start': !isGroupHeader,
									})}
								>
									{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
								</th>
								);
							})}
						</tr>
					))}
				</thead>
				<tbody>
					{Array.from({ length: rowCount }, (_, rowIndex) => (
						<tr key={rowIndex}>
							{leafColumns.map((column) => {
								const cellProps = column.columnDef.meta?.cellProps ?? {};
								const isCompact = column.id === 'selection' || column.id === 'expand';
								const isAction = column.id === 'actions' || column.id === 'action';
								const barWidth = !isCompact && !isAction ? resolveSkeletonBarWidth(column) : null;
								return (
									<td key={column.id} {...cellProps} className={classNames(cellProps.className, 'align-middle text-start')}>
										<span
											className={classNames('app-workbench-row-skeleton-bar', {
												'app-workbench-row-skeleton-bar--check': isCompact,
												'app-workbench-row-skeleton-bar--short': isAction,
											})}
											style={barWidth ? { width: `${barWidth}px`, maxWidth: '100%' } : undefined}
										/>
									</td>
								);
							})}
						</tr>
					))}
				</tbody>
			</Table>
		</div>
	);
};

/** 桌面列表加载/翻页/刷新：顶部条形脉冲；首屏无行时用表行骨架；有行时表格半透明 */
export const WorkbenchAdvanceTableFetchingBody = ({ table, children, className }) => {
	const visible = useWorkbenchListFetchingVisible(table);
	const { isLoading } = getTableListFetchingState(table);
	const dataLength = Array.isArray(table?.options?.data) ? table.options.data.length : 0;
	const hasRows = dataLength > 0;
	const isPageFetching = visible && hasRows;
	const showRowSkeleton = isLoading && !hasRows;

	return (
		<>
			<WorkbenchListProgressBar visible={visible} className={classNames('app-workbench-advance-table-progress', className)} />
			<div
				className={classNames('flex-grow-1 min-h-0 overflow-auto app-workbench-advance-table-body', {
					'is-list-fetching': isPageFetching,
				})}
				aria-busy={visible || undefined}
			>
				{showRowSkeleton ? <WorkbenchDesktopRowSkeleton table={table} /> : children}
			</div>
		</>
	);
};
